<template>
  <article
    class="par-card"
    :style="{ animationDelay: `${index * 0.08}s` }"
  >
    <div class="par-card__media">
      <span v-if="item.fitter_only" class="par-card__badge par-card__badge--fitter">
        Certified fitter
      </span>
      <img
        :src="imageSrc"
        :alt="item.name"
        class="par-card__img"
        loading="lazy"
        decoding="async"
        @error="imageFailed = true"
      />
    </div>

    <div class="par-card__body">
      <p class="par-card__meta">
        <span>{{ item.category }}</span>
        <span aria-hidden="true">·</span>
        <span>{{ item.type }}</span>
      </p>
      <h3 class="par-card__title">{{ item.name }}</h3>
      <p v-if="item.brand" class="par-card__brand">{{ item.brand }}</p>
      <p class="par-card__desc">{{ item.short_description || "Adaptive sports equipment" }}</p>

      <div class="par-card__price">
        <span class="par-card__price-now">Ksh {{ formatPrice(item.price) }}</span>
      </div>

      <p class="par-card__stock" :class="{ 'par-card__stock--out': !item.is_in_stock }">
        <i :class="item.is_in_stock ? 'fa-solid fa-circle-check' : 'fa-solid fa-clock'" />
        {{ item.is_in_stock ? "In stock" : "Made to order" }}
      </p>
    </div>

    <div class="par-card__actions">
      <button type="button" class="par-btn par-btn--primary" @click="$emit('learn-more', item)">
        Learn More
      </button>
      <button
        type="button"
        class="par-btn par-btn--ghost"
        :disabled="orderPending"
        @click="$emit('make-order', item)"
      >
        Make Order
      </button>
    </div>
  </article>
</template>

<script>
import { buildApiUrl } from "@/config/api";

const PLACEHOLDER = buildApiUrl("/static/images/product_images/placeholder.png");

export default {
  name: "ParalympicEquipmentCard",
  props: {
    item: { type: Object, required: true },
    index: { type: Number, default: 0 },
    orderPending: { type: Boolean, default: false },
  },
  emits: ["learn-more", "make-order"],
  data() {
    return { imageFailed: false };
  },
  computed: {
    imageSrc() {
      if (this.imageFailed || !this.item.thumbnail) return PLACEHOLDER;
      if (typeof this.item.thumbnail === "string" && this.item.thumbnail.startsWith("http")) {
        return this.item.thumbnail;
      }
      return this.item.thumbnail || PLACEHOLDER;
    },
  },
  methods: {
    formatPrice(value) {
      return Number(value || 0).toLocaleString("en-KE");
    },
  },
};
</script>

<style scoped>
.par-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--background-color1);
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--background-color3);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
  opacity: 0;
  animation: parFadeInUp 0.65s ease forwards;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.par-card:hover {
  transform: translateY(-4px);
  border-color: var(--border-color-hover);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
}

.par-card__media {
  position: relative;
  height: 168px;
  background: var(--background-color2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.par-card__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.65rem;
  transition: transform 0.3s ease;
}

.par-card:hover .par-card__img {
  transform: scale(1.04);
}

.par-card__badge {
  position: absolute;
  top: 0.55rem;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.par-card__badge--fitter {
  right: 0.55rem;
  background: rgba(var(--border-color-hover-rgb, 37, 99, 235), 0.92);
  color: #fff;
}

.par-card__body {
  flex: 1;
  padding: 0.85rem 0.95rem 0.5rem;
  text-align: center;
}

.par-card__meta {
  margin: 0 0 0.35rem;
  font-size: 0.68rem;
  color: var(--text-color2);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  justify-content: center;
}

.par-card__title {
  margin: 0 0 0.25rem;
  font-size: clamp(0.9rem, 2.8vw, 1rem);
  font-weight: 700;
  color: var(--text-color1);
  line-height: 1.3;
}

.par-card__brand {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  color: var(--border-color-hover);
  font-weight: 600;
}

.par-card__desc {
  margin: 0 0 0.65rem;
  font-size: 0.78rem;
  color: var(--text-color2);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.par-card__price {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  align-items: center;
  margin-bottom: 0.45rem;
}

.par-card__price-now {
  font-size: clamp(0.95rem, 3vw, 1.05rem);
  font-weight: 700;
  color: var(--border-color-hover);
}

.par-card__stock {
  margin: 0;
  font-size: 0.74rem;
  color: #2e7d32;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.par-card__stock--out {
  color: var(--text-color2);
}

.par-card__actions {
  display: flex;
  gap: 0.5rem;
  padding: 0 0.85rem 0.95rem;
  margin-top: auto;
}

.par-btn {
  flex: 1;
  min-width: 0;
  padding: 0.55rem 0.65rem;
  border-radius: 999px;
  font-size: clamp(0.72rem, 2.4vw, 0.8rem);
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.par-btn--primary {
  background: var(--border-color-hover);
  color: var(--background-color2);
}

.par-btn--primary:hover {
  transform: translateY(-2px);
}

.par-btn--ghost {
  background: transparent;
  color: var(--text-color1);
  border: 1px solid var(--text-color1);
}

.par-btn--ghost:hover:not(:disabled) {
  background: var(--text-color1);
  color: var(--background-color2);
  transform: translateY(-2px);
}

.par-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@keyframes parFadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
